/**
 * Formatting Utilities
 * Common formatting functions for dates, currency, file sizes, etc.
 */

/**
 * Format a date value for display
 * @param {string|Date} date - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '-';

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '-';

    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options,
    };

    return new Intl.DateTimeFormat('en-ZA', defaultOptions).format(dateObj);
  } catch {
    return '-';
  }
};

/**
 * Format a date with time
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Deterministic numeric format: '.' decimal separator, space thousands separator.
// The en-ZA locale otherwise emits ',' as decimal which is visually confusable
// with the space thousands separator (e.g., "8 000,000" reads as eight million).
const formatNumberDeterministic = (numValue, decimals) => {
  const fixed = Math.abs(numValue).toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const body = decPart ? `${withThousands}.${decPart}` : withThousands;
  return numValue < 0 ? `-${body}` : body;
};

/**
 * Format a currency value
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., 'ZAR', 'USD')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'ZAR', decimals = null) => {
  if (amount === null || amount === undefined) return '-';

  try {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '-';

    const dp = decimals !== null ? decimals : 2;
    return `${currency || 'ZAR'} ${formatNumberDeterministic(numAmount, dp)}`;
  } catch {
    const dp = decimals !== null ? decimals : 2;
    return `${currency || 'ZAR'} ${parseFloat(amount || 0).toFixed(dp)}`;
  }
};

/**
 * Format a number with thousand separators
 * @param {number} value - Number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';

  try {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';

    return formatNumberDeterministic(numValue, decimals);
  } catch {
    return value?.toString() || '-';
  }
};

/**
 * Format a percentage value
 * @param {number} value - Percentage value (e.g., 5.5 for 5.5%)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';

  try {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '-';

    return `${numValue.toFixed(decimals)}%`;
  } catch {
    return '-';
  }
};

/**
 * Format a file size in bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);

  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
};

/**
 * Format a phone number
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhone = (phone) => {
  if (!phone) return '-';

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format South African numbers
  if (cleaned.length === 10 && cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }

  // Format international numbers
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, -9)} ${cleaned.slice(-9, -6)} ${cleaned.slice(-6, -3)} ${cleaned.slice(-3)}`;
  }

  return phone;
};

/**
 * Truncate text to a specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format a reference number with leading zeros
 * @param {number} num - Number to format
 * @param {number} length - Desired length
 * @returns {string} Formatted reference with leading zeros
 */
export const formatReference = (num, length = 6) => {
  return String(num).padStart(length, '0');
};

export default {
  formatDate,
  formatDateTime,
  formatCurrency,
  formatNumber,
  formatPercent,
  formatFileSize,
  formatPhone,
  truncateText,
  formatReference,
};
